import CheckmarkFilled16 from '@carbon/icons-react/es/checkmark--filled/16';
import Close16 from '@carbon/icons-react/es/close/16';
import { notification } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { UserAPI } from 'api/user';
import { Button, LinkButton } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { logout } from 'utils/logout';

const ChangePasswordModal = styled(Modal)`
  .ant-modal-footer {
    text-align: left;
    display: flex;
    align-items: center;
  }

  ${LinkButton} {
    flex: 1;
  }
`;

export const ChangePasswordSetting: FC = () => {
  const [form] = Form.useForm();
  const { user } = useUser();
  const intl = useIntl();

  const changeNameTitle = useTranslation('change_password');
  const oldPasswordLabel = useTranslation(
    'settings.profile.change_password.old_password',
  );
  const newPasswordLabel = useTranslation(
    'settings.profile.change_password.new_password',
  );
  const confirmPasswordLabel = useTranslation(
    'settings.profile.change_password.confirm_password',
  );
  const successToastMsg = useTranslation(
    'settings.profile.change_password.success',
  );
  const passwordMatchText = useTranslation(
    'settings.profile.change_password.validations.password_match',
  );
  const forgotPasswordSuccessToast = intl.formatMessage(
    {
      id: 'settings.profile.change_password.request_forgot_password',
    },
    {
      email: user.emailId,
    },
  );

  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  }, [form, user.firstName, user.lastName]);

  const changePassword = (payload: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    UserAPI.changePassword({
      user,
      password: payload,
    })
      .then(() => {
        notification.success({
          message: successToastMsg,
          closeIcon: <Close16 />,
          placement: 'bottomLeft',
          duration: 3,
          icon: <CheckmarkFilled16 color="var(--success-color)" />,
        });

        logout();
      })
      .catch((error) => {
        notification.error({
          message: error.response.data.errors[0].reason,
          placement: 'bottomLeft',
          duration: 10,
        });
      });
  };

  const onOk = () => {
    form.validateFields().then((values) => {
      form.resetFields();
      changePassword(values);
    });
  };
  const onCancel = () => setIsModalVisible(false);

  const handleForgotPasswordClick = () => {
    UserAPI.forgotPassword(user.userId)
      .then(() => {
        notification.success({
          message: forgotPasswordSuccessToast,
          closeIcon: <Close16 />,
          placement: 'bottomLeft',
          duration: 3,
          icon: <CheckmarkFilled16 color="var(--success-color)" />,
        });
      })
      .catch(() => {
        notification.error({
          message: 'Something went wrong!',
          placement: 'bottomLeft',
          duration: 10,
        });
      });
  };

  return (
    <>
      <LinkButton onClick={() => setIsModalVisible(true)}>
        <FormattedMessage id="change_password" />
      </LinkButton>
      <ChangePasswordModal
        title={changeNameTitle}
        visible={isModalVisible}
        onCancel={onCancel}
        footer={[
          <LinkButton onClick={handleForgotPasswordClick}>
            <FormattedMessage id="settings.profile.change_password.forgot_password" />
          </LinkButton>,
          <Button key="back" onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>,
          <Button key="submit" type="primary" onClick={onOk}>
            <FormattedMessage id="submit" />
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="oldPassword"
            label={oldPasswordLabel}
            rules={[
              {
                required: true,
                message: useTranslation(
                  'settings.profile.change_password.validations.old_password',
                ),
              },
            ]}
          >
            <Input type="password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label={newPasswordLabel}
            rules={[
              {
                required: true,
                message: useTranslation(
                  'settings.profile.change_password.validations.new_password',
                ),
              },
              {
                min: 8,
                message: useTranslation(
                  'settings.profile.change_password.validations.new_password.length',
                ),
              },
            ]}
          >
            <Input type="password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label={confirmPasswordLabel}
            rules={[
              {
                required: true,
                message: useTranslation(
                  'settings.profile.change_password.validations.confirm_password',
                ),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(passwordMatchText);
                },
              }),
            ]}
          >
            <Input type="password" />
          </Form.Item>
        </Form>
      </ChangePasswordModal>
    </>
  );
};
